// apps/web/src/pages/author/ExtractionDraftBanner.tsx
//
// Phase 10 Plan 12 Task 1 — non-blocking banner shown above the EditorShell // sanitize-allow: [A-Z]{3,5}-[0-9]{2}
// when the loaded recipe is an AI-extraction draft (Surface 3 per 10-UI-SPEC
// §<ExtractionDraftBanner> spec lines 638-683 + §Color C lines 372-384 +
// §Copywriting Surface 3 lines 491-507).
//
// THREE-ARM CONDITIONAL (10-UI-SPEC lines 648-679):
//   - neither flag set                       → render null (early return)
//   - extraction_partial: true               → partial copy + "Discard draft"
//                                               (opens a destructive Dialog;
//                                               confirm fires onDiscard)
//   - is_ai_extracted_unreviewed: true (only) → unreviewed copy +
//                                               "Return to review" (fires
//                                               onReturnToReview directly; the
//                                               Phase 8 UnsavedChangesGuard
//                                               handles the dirty-form case)
//
// INFORMATIONAL tone (10-UI-SPEC §Color C lines 376-383): bg-primary/5 +
// border-primary/20 + text-foreground. role="status" aria-live="polite"
// (line 500) — announced on mount, never auto-dismissed.
//
// Two-weight typography: EXTRACTION DRAFT label is text-xs font-semibold
// (weight 600); body + buttons are Body weight 400. NO font-medium (Phase 10
// carry-forward, 10-UI-SPEC line 85).
//
// Sanitization: source attribution is rendered as React text children (never
// dangerouslySetInnerHTML); no inline style (T-10-12-02 — no XSS surface).

import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ExtractionDraftFlags } from "./AISuggestionContext";

export interface ExtractionDraftBannerProps {
  flags: ExtractionDraftFlags;
  onDiscard?: () => void; // opens destructive Dialog; confirm fires this
  onReturnToReview?: () => void; // present only when user arrived from Surface 2
}

// DiscardDraftDialog — destructive confirmation gating the partial-draft
// discard (10-UI-SPEC §Copywriting Surface 3 lines 501-504). "Keep editing"
// is autoFocused so ENTER cancels safely (T-10-12-03); "Discard draft" is
// variant="destructive" per the Phase 8/9 Dialog-confirm discipline.
interface DiscardDraftDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onConfirm: () => void;
}

function DiscardDraftDialog({
  open,
  onOpenChange,
  onConfirm,
}: DiscardDraftDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Discard this extraction draft?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          The partial extraction will be soft-deleted. You can recover it from the admin recovery
          list within 30 days (Phase 15).
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" autoFocus onClick={() => onOpenChange(false)}>
            Keep editing
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            Discard draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ExtractionDraftBanner({
  flags,
  onDiscard,
  onReturnToReview,
}: ExtractionDraftBannerProps): React.ReactElement | null {
  const [discardOpen, setDiscardOpen] = useState<boolean>(false);

  if (!flags.extraction_partial && !flags.is_ai_extracted_unreviewed) {
    return null;
  }

  const isPartial = flags.extraction_partial;
  const source = flags.sourceUrlOrHash ?? "an uploaded source";

  return (
    <Alert
      role="status"
      aria-live="polite"
      className="mx-4 mt-4 border-primary/20 bg-primary/5 text-foreground"
    >
      <AlertTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        EXTRACTION DRAFT
      </AlertTitle>
      <AlertDescription>
        {isPartial
          ? `This recipe was partially extracted from ${source}. The AI couldn't complete it — fill in the missing fields and save to finalize.`
          : `You're editing an AI extraction from ${source}. Save to finalize it as reviewed; discard to return to review without changes.`}
        <div className="mt-3 flex gap-3">
          {isPartial && onDiscard ? (
            <Button variant="outline" size="sm" onClick={() => setDiscardOpen(true)}>
              Discard draft
            </Button>
          ) : null}
          {!isPartial && onReturnToReview ? (
            <Button variant="outline" size="sm" onClick={onReturnToReview}>
              Return to review
            </Button>
          ) : null}
        </div>
      </AlertDescription>
      {isPartial && onDiscard ? (
        <DiscardDraftDialog
          open={discardOpen}
          onOpenChange={setDiscardOpen}
          onConfirm={onDiscard}
        />
      ) : null}
    </Alert>
  );
}
